import { jsx as _jsx } from "react/jsx-runtime";
import { API_URL } from '@hellodarwin/core/lib/api';
import LoadingScreen from '@hellodarwin/core/lib/components/loading/screen';
import { useNavigate } from '@hellodarwin/core/lib/features/providers/location-provider';
import { createContext, useCallback, useContext, useMemo, } from 'react';
import useHandleLogin from './hooks/use-handle-login';
export const EMAIL_VERIFIED_CUSTOM_CLAIM = `${API_URL}/email_verified`;
const STABLE_EMAIL_VERIFIED_CUSTOM_CLAIM = 'https://api.hellodarwin.com/email_verified';
const decodeJwtPayload = (token) => {
    const payload = token.split('.')[1];
    if (!payload)
        return {};
    try {
        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4), '=');
        return JSON.parse(globalThis.atob(paddedPayload));
    }
    catch (_a) {
        return {};
    }
};
const getStringClaim = (claims, claim) => {
    const value = claims[claim];
    return typeof value === 'string' ? value : '';
};
export const LoginContext = createContext({
    handleLogout: function () {
        throw new Error('Function not implemented.');
    },
    refreshApp: function () {
        throw new Error('Function not implemented.');
    },
    forceReauthentication: async function () {
        throw new Error('Function not implemented.');
    },
    refreshAccessToken: async function () {
        throw new Error('Function not implemented.');
    },
    accessToken: '',
});
LoginContext.displayName = 'LoginContext';
export const DEV_ACCESS_TOKEN_KEY = 'dev_access_token';
const readDevAccessToken = () => {
    if (process.env.NODE_ENV !== 'development')
        return null;
    if (typeof window === 'undefined')
        return null;
    try {
        return window.localStorage.getItem(DEV_ACCESS_TOKEN_KEY);
    }
    catch (_a) {
        return null;
    }
};
const DevImpersonationProvider = ({ accessToken, children, }) => {
    const navigate = useNavigate();
    const loginContextValue = useMemo(() => ({
        accessToken,
        user: {
            name: 'Dev impersonation',
            email: 'dev@localhost',
            email_verified: true,
        },
        handleLogout: () => {
            window.localStorage.removeItem(DEV_ACCESS_TOKEN_KEY);
            window.location.reload();
        },
        refreshApp: () => navigate(0),
        forceReauthentication: async () => undefined,
        refreshAccessToken: async () => accessToken,
    }), [accessToken, navigate]);
    return (_jsx(LoginContext.Provider, { value: loginContextValue, children: children }));
};
const Auth0LoginProvider = ({ children, customTokenStorageKey, EmailVerificationScreenComponent, localStorageKeys, requireEmailVerification, resendEmailVerification, }) => {
    const navigate = useNavigate();
    const { forceReauthentication, handleLogout, refreshAccessToken, accessToken, isLoading, user, } = useHandleLogin(customTokenStorageKey, localStorageKeys);
    const refreshApp = useCallback(() => {
        navigate(0);
    }, [navigate]);
    const loginContextValue = useMemo(() => ({
        handleLogout,
        user,
        accessToken,
        refreshApp,
        forceReauthentication,
        refreshAccessToken,
    }), [
        accessToken,
        forceReauthentication,
        handleLogout,
        refreshAccessToken,
        refreshApp,
        user,
    ]);
    const isLoginLoading = useMemo(() => isLoading || !accessToken || !user, [isLoading, accessToken, user]);
    if (isLoginLoading)
        return _jsx(LoadingScreen, {});
    const userClaims = user;
    const accessTokenClaims = decodeJwtPayload(accessToken);
    const isEmailVerified = userClaims.email_verified === true ||
        userClaims[EMAIL_VERIFIED_CUSTOM_CLAIM] === true ||
        userClaims[STABLE_EMAIL_VERIFIED_CUSTOM_CLAIM] === true ||
        accessTokenClaims.email_verified === true ||
        accessTokenClaims[EMAIL_VERIFIED_CUSTOM_CLAIM] === true ||
        accessTokenClaims[STABLE_EMAIL_VERIFIED_CUSTOM_CLAIM] === true;
    const email = getStringClaim(userClaims, 'email') ||
        getStringClaim(accessTokenClaims, 'email');
    if (requireEmailVerification && !isEmailVerified) {
        if (!EmailVerificationScreenComponent)
            return _jsx(LoadingScreen, {});
        return (_jsx(LoginContext.Provider, { value: loginContextValue, children: _jsx(EmailVerificationScreenComponent, { accessToken: accessToken, email: email, handleLogout: handleLogout, refreshEmailVerification: () => refreshAccessToken(), resendEmailVerification: resendEmailVerification }) }));
    }
    return (_jsx(LoginContext.Provider, { value: loginContextValue, children: children }));
};
const LoginProvider = (props) => {
    const devAccessToken = readDevAccessToken();
    if (devAccessToken) {
        return (_jsx(DevImpersonationProvider, { accessToken: devAccessToken, children: props.children }));
    }
    return _jsx(Auth0LoginProvider, Object.assign({}, props));
};
export const useLogin = () => useContext(LoginContext);
export default LoginProvider;
